import { create } from 'zustand';
import { useEffect } from 'react';

export type ToastTone = 'info' | 'success' | 'error';

interface Toast {
  id: number;
  text: string;
  tone: ToastTone;
  durationMs: number;
}

interface ToastStore {
  items: Toast[];
  /**
   * 1.5.B-Polish-UX Enh-FC-9: durationMs optional override. Когда нужно длинное
   * уведомление (e.g. «Публикация в процессе...» — пока polling site.status
   * не вернёт final state) — передавать большое число (e.g. 30_000) или Infinity
   * для toast'а без auto-dismiss (юзер закрывает кликом).
   */
  push: (text: string, tone?: ToastTone, durationMs?: number) => number;
  dismiss: (id: number) => void;
}

let _id = 1;

// 1.5.C-UX-iter2 Phase 7 BUG-26 fix — лимит 3 одновременных тостов (LIFO drop старого).
const MAX_TOASTS = 3;

// eslint-disable-next-line react-refresh/only-export-components
export const useToastStore = create<ToastStore>((set, get) => ({
  items: [],
  push: (text, tone = 'info', durationMs) => {
    const id = _id++;
    // 1.5.C-UX-iter2 Phase 7 BUG-26 fix: error toasts 10s (было 6s),
    // success/info 3s. Error tone живёт дольше + explicit ✕ button (UI ниже).
    const defaultDuration = tone === 'error' ? 10000 : 3000;
    const duration = durationMs ?? defaultDuration;
    set((s) => {
      const next = [...s.items, { id, text, tone, durationMs: duration }];
      // BUG-26 fix: лимит 3 одновременных — drop oldest когда добавляется 4й.
      return { items: next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next };
    });
    if (Number.isFinite(duration) && duration > 0) {
      setTimeout(() => get().dismiss(id), duration);
    }
    return id;
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

// eslint-disable-next-line react-refresh/only-export-components
export function toast(text: string, tone: ToastTone = 'info', durationMs?: number): number {
  return useToastStore.getState().push(text, tone, durationMs);
}

// eslint-disable-next-line react-refresh/only-export-components
export function dismissToast(id: number): void {
  useToastStore.getState().dismiss(id);
}

export function ToastViewport() {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);
  useEffect(() => {}, [items]);
  return (
    // 1.5.B-Polish-UX Enh-FC-9: z-[60] (выше дефолтных модалов z-50) чтобы
    // публикационные toasts не перекрывались модалкой подтверждения.
    <div className="fixed bottom-4 right-4 z-[60] space-y-2 max-w-sm">
      {items.map((t) => (
        // 1.5.C-UX-iter2 Phase 7 BUG-26 fix: явный ✕ button (особенно для error
        // тостов с 10s lifetime — Юрий может закрыть раньше).
        <div
          key={t.id}
          className={`flex items-start gap-2 px-3 py-2 rounded-md shadow-md text-sm ${
            t.tone === 'error'
              ? 'bg-rose-600 text-white'
              : t.tone === 'success'
              ? 'bg-emerald-600 text-white'
              : 'bg-[#15203A] text-white'
          }`}
        >
          <span className="flex-1">{t.text}</span>
          <button
            type="button"
            onClick={() => dismiss(t.id)}
            className="shrink-0 opacity-70 hover:opacity-100 text-base leading-none -mt-0.5"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
